import type { ToolDefinition, ToolType } from "../types";

const aiPdfAnalysis: ToolDefinition = {
  slug: "ai-pdf-analysis",
  name: "Snappy AI PDF Analysis",
  title: "AI PDF Analyzer: Structured Document Intelligence & Metadata",
  description:
    "Run an AI PDF analysis online to extract structured JSON data. Analyze PDF metadata, text statistics, page sizes, fonts, annotations, and JavaScript risk signals.",
  longDescription:
    "Upload your PDF to generate a comprehensive, AI-ready document intelligence report. Our online PDF analyzer inspects deep file metadata, estimates page dimensions, calculates text statistics, and flags interactive elements like forms and JavaScript. By turning your document into structured JSON data, this tool helps you verify file properties before feeding them into RAG pipelines, AI summarization workflows, or automated archiving systems.",
  toolType: {
    label: "PDF Analyzer",
    icon: "FileSearch",
  },
  heroBadges: [
    "100% Free",
    "Structured JSON Output",
    "No Account Required",
    "AI Preflight Ready",
    "Private Processing",
  ],
  category: "Analysis Tools",
  categorySlug: "image-analysis-tools",
  href: "/tools/ai-pdf-analysis",
  api: "/api/v1/pdf/analysis",
  keywords: [
    "snappy ai pdf analysis",
    "document analyzer online",
    "ai ready report tool",
    "metadata viewer for files",
    "check fonts and images",
    "document intelligence inspector",
    "structured file analysis",
    "security signal checker",
    "text statistics utility",
    "inspect portable document properties",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Run an AI PDF Analysis Online",
        description:
          "Extract structured document intelligence from your PDF in four simple steps to prepare it for your downstream automation and AI pipelines.",
      },
      benefits: {
        title: "Why Use a Structured PDF Analyzer?",
        description:
          "Standard PDF readers only show you the visual layer. A dedicated PDF structure analyzer provides the raw data points, text statistics, and risk signals needed for robust document automation.",
      },
      faqs: {
        title: "Frequently Asked Questions About PDF Document Intelligence",
        description:
          "Learn more about reading PDF metadata, calculating text statistics, and understanding the limitations of automated PDF parsing.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        title: "Deep Document Intelligence for AI Workflows",
        level: "h2",
        paragraphs: [
          "Before feeding documents into an AI summarization tool or a Retrieval-Augmented Generation (RAG) pipeline, you must understand exactly what is inside the file structure. Our AI PDF analysis tool acts as a comprehensive preflight checker and PDF metadata viewer, tearing down the file architecture to provide an AI-ready PDF report in a structured JSON format. Instead of just displaying visual pages like an online PDF reader, this tool inspects the underlying data, annotations, and text volume.",
          "You can easily analyze a PDF file online to reveal hidden metadata, inspect font encodings, count embedded images, and verify estimated page dimensions. By running a structured PDF analysis, you gain immediate insights into the document's total character and word count, which helps you decide if a PDF is a scanned image requiring OCR, or a text-based document ready for instant extraction. It also employs targeted heuristics to flag potential interactivity risks, such as annotations containing 'javascript' or form 'widgets'.",
        ],
        note: "This tool generates a rich JSON data report describing the PDF structure. It does not rewrite, summarize, or alter the original document text.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Core Intelligence Data Extracted",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Document Properties & Metadata",
                icon: "Info",
                description:
                  "View title, subject, author, keywords, creator, producer, trapped status, and precise creation or modification dates.",
                accent: "primary",
              },
              {
                label: "Text Statistics & Page Structure",
                icon: "Layout",
                description:
                  "Check PDF version, file size, page counts, tagged or linearized flags, point and millimeter dimensions, rotation, and text volume.",
                accent: "primary",
              },
              {
                label: "Risk Signals & Interactivity",
                icon: "ShieldAlert",
                description:
                  "Detect encryption, security method, permission flags, bookmarks, forms, JavaScript annotations, and embedded image assets.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Structured PDF Analysis Data Reference",
            description:
              "A breakdown of the structured data points returned when you analyze a PDF for AI processing, along with current engine capabilities.",
            columns: [
              "Data Category",
              "Key Fields Inspected",
              "Implementation Details & AI Relevance",
            ],
            rows: [
              {
                "Data Category": "Basic Metadata",
                "Key Fields Inspected":
                  "Title, Subject, Author, Keywords, Creator, Producer, Creation Date, Modified Date, Trapped",
                "Implementation Details & AI Relevance":
                  "Helps index the document correctly. Read directly from the standard PDF /Info dictionary.",
              },
              {
                "Data Category": "File Structure",
                "Key Fields Inspected":
                  "PDF Version, Page Count, File Size, Linearized, Tagged PDF",
                "Implementation Details & AI Relevance":
                  "Identifies document generation targets, upload weight, streaming readiness, accessibility tagging, and the total page surface for downstream workflows.",
              },
              {
                "Data Category": "Text Statistics",
                "Key Fields Inspected":
                  "Total Characters, Total Words, Text Bytes, Language Hints",
                "Implementation Details & AI Relevance":
                  "Crucial for AI pipelines: low text stats on a high-page PDF indicates a scanned document requiring OCR.",
              },
              {
                "Data Category": "Page Structure",
                "Key Fields Inspected":
                  "Page Number, Width/Height (pts), Width/Height (mm), Rotation",
                "Implementation Details & AI Relevance":
                  "Dimensions are estimated from character bounding boxes, while rotation is exposed for layout diagnostics when the parser reports it.",
              },
              {
                "Data Category": "Security",
                "Key Fields Inspected":
                  "Encrypted, Security Method, Printing, Modifying, Copying, Commenting",
                "Implementation Details & AI Relevance":
                  "Shows whether a document is protected and surfaces permission flags that can affect extraction, printing, review, and automation.",
              },
              {
                "Data Category": "Annotations & Forms",
                "Key Fields Inspected":
                  "Annotation Page, Subtype, Content, Author, Date, Forms, JavaScript",
                "Implementation Details & AI Relevance":
                  "Flags interactivity using heuristics (e.g., checking if annotation subtypes contain 'widget' or 'javascript').",
              },
              {
                "Data Category": "Fonts & Images",
                "Key Fields Inspected":
                  "Font Name, Font Type, Embedded, Subset, Encoding, Base Font, Image Count, Image Page, Pixel Size, Format, Color Space, Bits per Component",
                "Implementation Details & AI Relevance":
                  "Extracted via character-level and resource data to explain visual fidelity, font decoding issues, and image-heavy PDF behavior.",
              },
              {
                "Data Category": "Bookmarks & Outline",
                "Key Fields Inspected":
                  "Has Bookmarks, Bookmark Title, Depth, Page Number",
                "Implementation Details & AI Relevance":
                  "Captures the document outline tree so automation can preserve navigation, section hierarchy, and jump targets.",
              },
              {
                "Data Category": "Extra & Runtime",
                "Key Fields Inspected":
                  "Raw XMP Metadata, Processing Time (ms)",
                "Implementation Details & AI Relevance":
                  "Provides optional raw metadata for audit trails and reports the wall-clock analysis time for performance monitoring.",
              },
            ],
            note: "Due to current PDF engine limitations, certain low-level flags (like detailed font embedding status, exact page rotation, permission internals, and raw XMP metadata) may return empty or placeholder values.",
            emphasisColumns: ["Data Category"],
            monospaceColumns: ["Key Fields Inspected"],
          },
        ],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your PDF file",
        description:
          "Select the PDF document you want to inspect. Use this tool before sending a file into an AI summarization script, search indexing pipeline, archiving system, or compliance review.",
      },
      {
        number: "02",
        title: "Click Analyze PDF",
        description:
          "Start the structured analysis. The backend parses the file to extract metadata, calculate page dimensions from character bounding boxes, read fonts, count images, and compile text statistics.",
      },
      {
        number: "03",
        title: "Review the AI-ready report",
        description:
          "Check the returned JSON data. Look closely at metadata, structure flags, security permissions, text statistics, font details, embedded images, annotation content, and bookmark outlines.",
      },
      {
        number: "04",
        title: "Decide the next workflow",
        description:
          "Use the structured insights to route the document. Run OCR for scanned image-only files, clean up hidden metadata, reject files with unwanted JavaScript, or send clean text direct to your AI pipeline.",
      },
    ],
    benefits: [
      {
        icon: "FileText",
        title: "Check PDF Text Statistics Online",
        description:
          "Instantly view total character, word, and text byte counts across the entire document. This is the fastest way to figure out if a PDF is a text-based document ready for RAG extraction, or just a wrapper for scanned images.",
      },
      {
        icon: "Search",
        title: "Deep PDF Metadata Viewer",
        description:
          "Go beyond basic operating system properties to inspect title, subject, author, creator, producer, creation dates, modification dates, document keywords, and trapped metadata. Perfect for document auditing and provenance checks.",
      },
      {
        icon: "ShieldCheck",
        title: "Review Encryption & Permissions",
        description:
          "See whether the PDF is encrypted, identify the reported security method, and review permission flags for printing, modifying, copying, and commenting before using the file in automated workflows.",
      },
      {
        icon: "AlertTriangle",
        title: "Detect JavaScript & Forms",
        description:
          "Run a preflight check to see if the PDF contains interactive or risky elements. The analyzer flags 'has_javascript' and 'has_forms' while also listing annotation page, subtype, content, author, and date when available.",
      },
      {
        icon: "Type",
        title: "Inspect Fonts & Embedded Images",
        description:
          "Analyze visual assets within the file. Check font names, font types, embedded/subset flags, base fonts, encodings, image count, image dimensions, format, color space, and bits per component.",
      },
      {
        icon: "Layers",
        title: "Extract Page Sizes & Bookmarks",
        description:
          "View detailed structural information including page numbers, point dimensions, millimeter dimensions, rotation, plus a hierarchical tree of document bookmarks with depth and resolved page numbers.",
      },
      {
        icon: "Timer",
        title: "Track Runtime & Extra Metadata",
        description:
          "Inspect optional raw XMP metadata when present and view processing time in milliseconds to understand how quickly the document was parsed.",
      },
    ],
    faqs: [
      {
        question:
          "What is the difference between a PDF reader and a PDF analyzer?",
        answer:
          "A standard PDF reader renders the visual layout of a document for a human to read. An AI PDF analyzer ignores visual rendering and instead extracts the underlying structural data—like metadata dictionaries, text statistics, annotation subtypes, and page dimensions—packaging it into a structured JSON report for AI and automation software.",
      },
      {
        question: "How do I analyze a PDF before using it with AI?",
        answer:
          "Upload your file to our AI PDF analyzer. It will immediately generate an AI-ready PDF report showing text statistics, structure, and security settings. If the word count is zero but the page count is high, you know the file is scanned and you need to run OCR before sending it to an AI summarizer.",
      },
      {
        question: "What metadata can be hidden inside a PDF file?",
        answer:
          "PDFs contain an '/Info' dictionary holding extensive data, including the software used to create it (Producer/Creator), the original author, exact timestamps for creation and modification, embedded keywords, and subjects. Our tool acts as a PDF metadata viewer to expose all of these fields.",
      },
      {
        question: "How do I know if a PDF is scanned or text-based?",
        answer:
          "The most reliable method is to check PDF text statistics online using this tool. If a document has 50 pages but only 10 total text characters, it is almost certainly a scanned, image-only PDF. If it has tens of thousands of characters and words, it contains extractable text.",
      },
      {
        question: "How does the analyzer detect forms and JavaScript?",
        answer:
          "The tool uses targeted heuristics. It scans the 'annotations' array of the document. If it finds an annotation subtype containing the word 'widget', it flags the document as having forms. If an annotation subtype contains 'javascript', it flags the file for potential script risks.",
      },
      {
        question:
          "Can I see page dimensions and embedded images in a PDF online?",
        answer:
          "Yes. The structured PDF analysis report calculates page width and height (in both points and millimeters) based on internal character bounding boxes. It also counts the total number of embedded images hidden within the document's resource dictionaries.",
      },
      {
        question: "Are all fields in the JSON report always populated?",
        answer:
          "No. PDF is a highly flexible format, and not all creators populate every field. Additionally, our current PDF engine treats some highly specific fields (like exact font subsetting flags, page rotation metrics, and raw XMP metadata) as placeholders or limits them based on extraction difficulty.",
      },
      {
        question: "Why do some PDFs fail text extraction in AI tools?",
        answer:
          "PDFs typically fail AI summarization or RAG pipelines for three main reasons: they are scanned images without actual text layers, they use custom font encodings that output unreadable gibberish when extracted, or they have security permissions that strictly block content copying. A structured analysis helps you diagnose these exact issues upfront.",
      },
    ],
  },
};

export default aiPdfAnalysis;
export { aiPdfAnalysis };
